import React from 'react';
import * as LucideIcons from 'lucide-react';
import { services } from '../data/mock';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

// Service background images
const getServiceImage = (serviceId) => {
  const images = {
    1: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085',
    2: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c',
    3: 'https://images.unsplash.com/photo-1610563166150-b34df4f3bcd6',
    4: 'https://images.pexels.com/photos/89955/pexels-photo-89955.jpeg',
    5: 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0',
    6: 'https://images.unsplash.com/photo-1561070791-2526d30994b5',
    7: 'https://images.unsplash.com/photo-1633356122544-f134324a6cee',
    8: 'https://images.unsplash.com/photo-1667984390535-6d03cff0b11a',
    9: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f',
    10: 'https://images.unsplash.com/photo-1455390582262-044cdead277a'
  };
  return images[serviceId] || images[1];
};

const ServicesPageComponent = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Hero Section */}
      <section className="relative pt-32 pb-12 overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-1 bg-purple-600/20 backdrop-blur-sm border border-purple-500/30 rounded-full text-sm text-purple-300 mb-4">
              OUR SERVICES
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6">
              Digital Solutions That Drive Results
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              From innovative web development to cutting-edge AI solutions, Invotage delivers comprehensive digital services that transform your business vision into reality. Partner with us to unlock your company's full potential in the digital landscape.
            </p>
          </div>
        </div>
      </section>

      {/* Services Slider */}
      <section className="py-12 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Swiper
            modules={[Pagination, Autoplay]}
            spaceBetween={30}
            slidesPerView={1}
            loop={true}
            pagination={{ clickable: true }}
            autoplay={{ 
              delay: 3000, 
              disableOnInteraction: false,
              pauseOnMouseEnter: true 
            }}
            breakpoints={{
              640: { slidesPerView: 2 },
              768: { slidesPerView: 3 },
              1024: { slidesPerView: 4 }
            }}
            className="services-swiper"
          >
            {services.map((service) => {
              const Icon = LucideIcons[service.icon];
              return (
                <SwiperSlide key={service.id}>
                  <Link to={service.link}>
                    <div className="group relative w-full aspect-square rounded-3xl overflow-hidden hover:scale-105 transition-all duration-300 hover:shadow-2xl hover:shadow-purple-500/30 cursor-pointer border border-purple-500/20">
                      {/* Background Image */}
                      <div className="absolute inset-0">
                        <img 
                          src={getServiceImage(service.id)} 
                          alt={service.title}
                          className="w-full h-full object-cover"
                        />
                        {/* Strong Dark Overlay for better text visibility */}
                        <div className="absolute inset-0 bg-gradient-to-br from-gray-900/90 via-black/85 to-gray-900/90"></div>
                      </div>
                      
                      {/* Hover gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-br from-purple-600/30 via-blue-600/30 to-purple-600/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                      {/* Content */}
                      <div className="relative z-10 p-6 flex flex-col items-center justify-center text-center h-full space-y-4">
                        {/* Icon */}
                        <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-blue-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-purple-500/50">
                          {Icon && <Icon className="h-8 w-8 text-white" />}
                        </div>

                        {/* Title */}
                        <h3 className="text-white font-bold text-xl">{service.title}</h3>

                        {/* Features */}
                        <ul className="space-y-2">
                          {service.features.map((feature, idx) => (
                            <li key={idx} className="text-gray-300 text-sm">
                              {feature}
                            </li>
                          ))}
                        </ul>

                        {/* Arrow Icon */}
                        <div className="pt-2">
                          <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-blue-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-all shadow-lg shadow-purple-500/50">
                            <ArrowRight className="h-5 w-5 text-white group-hover:translate-x-1 transition-transform" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Why Choose Invotage?
            </h2>
            <div className="h-1 w-32 bg-gradient-to-r from-purple-500 to-blue-500 mx-auto rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl p-8 border border-purple-500/20 text-center hover:shadow-lg hover:shadow-purple-900/50 transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <LucideIcons.Award className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Expert Team</h3>
              <p className="text-gray-400">
                Seasoned professionals with decades of combined experience in cutting-edge technologies.
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl p-8 border border-purple-500/20 text-center hover:shadow-lg hover:shadow-purple-900/50 transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <LucideIcons.Zap className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Fast Delivery</h3>
              <p className="text-gray-400">
                Agile methodologies ensure rapid development without compromising quality.
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl p-8 border border-purple-500/20 text-center hover:shadow-lg hover:shadow-purple-900/50 transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <LucideIcons.HeadphonesIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">24/7 Support</h3>
              <p className="text-gray-400">
                Round-the-clock assistance to keep your business running smoothly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 rounded-3xl p-12 border border-purple-500/20 shadow-2xl shadow-purple-900/50">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Let's discuss your project and bring your vision to life with our expert digital solutions.
            </p>
            <Link to="/contact">
              <button className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-8 py-4 text-lg rounded-full shadow-lg shadow-purple-500/50 transition-all inline-flex items-center gap-2">
                Get Started Today
                <ArrowRight className="h-5 w-5" />
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPageComponent;
